import React from "react";
import { Box } from "@mui/material";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";
import { SkeletonStat } from "./Skeletons";

/**
 * StatCard — metric card for dashboard overviews.
 *
 * Props:
 *   icon:    lucide component
 *   label:   string (caption — uppercased)
 *   value:   string | number
 *   delta:   { value, direction: "up"|"down"|"flat", period }
 *   accent:  "slate" | "orange"  (icon container tint — default "slate")
 *   loading: boolean
 */

const ACCENTS = {
  slate: { bg: "#f1f5f9", fg: "#475569" },
  orange: { bg: "#fff7ed", fg: "#c2410c" },
};

const DELTA_VARIANTS = {
  up: { bg: "#ecfdf5", fg: "#047857", Icon: ArrowUp },
  down: { bg: "#fef2f2", fg: "#b91c1c", Icon: ArrowDown },
  flat: { bg: "#f1f5f9", fg: "#475569", Icon: Minus },
};

function StatCard({
  icon: Icon,
  label,
  value,
  delta,
  accent = "slate",
  loading = false,
}) {
  if (loading) return <SkeletonStat />;

  const iconColors = ACCENTS[accent] || ACCENTS.slate;
  // Hide delta chip entirely when value is 0 — avoids the "— 0%" noise in empty states.
  const deltaVisible = delta && (delta.value !== 0 || delta.direction !== "flat");
  const deltaCfg = deltaVisible ? DELTA_VARIANTS[delta.direction] || DELTA_VARIANTS.flat : null;

  return (
    <Box
      sx={{
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        backgroundColor: "#ffffff",
        p: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        height: "100%",
        width: "100%",
        boxSizing: "border-box",
        boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
        transition: "border-color 150ms ease-out",
        "&:hover": { borderColor: "#cbd5e1" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: "#64748b",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {label}
        </Box>
        {Icon && (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              backgroundColor: iconColors.bg,
              color: iconColors.fg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon size={16} strokeWidth={2.25} />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          fontSize: 28,
          fontWeight: 700,
          color: "#0f172a",
          lineHeight: 1.15,
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </Box>

      {/* Always render so all cards share the same height */}
      <Box sx={{ minHeight: 22, display: "flex", alignItems: "center", gap: "8px" }}>
        {deltaCfg && (
          <>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "2px",
                px: "6px",
                py: "1px",
                borderRadius: "6px",
                fontSize: 12,
                fontWeight: 600,
                backgroundColor: deltaCfg.bg,
                color: deltaCfg.fg,
              }}
            >
              <deltaCfg.Icon size={12} strokeWidth={2.5} />
              {delta.value}%
            </Box>
            {delta.period && (
              <Box sx={{ fontSize: 12, color: "#94a3b8" }}>{delta.period}</Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

export default StatCard;
